import {
  useId,
  useState,
  type DragEvent,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { InlineError } from "./Feedback";

export interface DropzoneProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children" | "onChange"
> {
  accept?: string;
  description?: ReactNode;
  disabled?: boolean;
  error?: ReactNode;
  id?: string;
  label: string;
  name?: string;
  onFileChange?: (file: File | null) => void;
}

function firstFileFromList(files: FileList): File | null {
  return files.length > 0 ? (files[0] ?? null) : null;
}

function firstFileFromDataTransfer(dataTransfer: DataTransfer): File | null {
  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) {
        return file;
      }
    }
  }

  return firstFileFromList(dataTransfer.files);
}

function hasFilePayload(dataTransfer: DataTransfer): boolean {
  return (
    Array.from(dataTransfer.types).includes("Files") ||
    Array.from(dataTransfer.items).some((item) => item.kind === "file") ||
    dataTransfer.files.length > 0
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Dropzone({
  accept,
  className,
  description,
  disabled = false,
  error,
  id,
  label,
  name,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileChange,
  ...props
}: DropzoneProps) {
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const labelId = `${inputId}-label`;
  const previewId = `${inputId}-preview`;
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const describedBy = [descriptionId, errorId, selectedFile ? previewId : null]
    .filter(Boolean)
    .join(" ");
  const classes = ["marker-dropzone"];

  if (isDragActive) {
    classes.push("marker-dropzone--dragging");
  }
  if (disabled) {
    classes.push("marker-dropzone--disabled");
  }
  if (error) {
    classes.push("marker-dropzone--invalid");
  }
  if (className) {
    classes.push(className);
  }

  function selectFile(file: File | null) {
    // Previews the first file only; the current consumer is the
    // single-file WAV upload.
    setSelectedFile(file);
    onFileChange?.(file);
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    onDragEnter?.(event);
    if (
      event.defaultPrevented ||
      disabled ||
      !hasFilePayload(event.dataTransfer)
    ) {
      return;
    }

    event.preventDefault();
    setIsDragActive(true);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    onDragOver?.(event);
    if (
      event.defaultPrevented ||
      disabled ||
      !hasFilePayload(event.dataTransfer)
    ) {
      return;
    }

    event.preventDefault();
    setIsDragActive(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    onDragLeave?.(event);
    if (event.defaultPrevented) {
      return;
    }

    setIsDragActive(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    onDrop?.(event);
    if (event.defaultPrevented || disabled) {
      return;
    }

    event.preventDefault();
    setIsDragActive(false);
    selectFile(firstFileFromDataTransfer(event.dataTransfer));
  }

  return (
    <div
      {...props}
      aria-labelledby={labelId}
      className={classes.join(" ")}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="group"
    >
      <input
        accept={accept}
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : undefined}
        aria-labelledby={labelId}
        className="marker-dropzone__input"
        disabled={disabled}
        id={inputId}
        name={name}
        onChange={(event) => {
          const files = event.currentTarget.files;
          selectFile(files ? firstFileFromList(files) : null);
        }}
        type="file"
      />
      <label className="marker-dropzone__target" htmlFor={inputId}>
        <span className="marker-dropzone__label" id={labelId}>
          {label}
        </span>
        {description ? (
          <span className="marker-dropzone__description" id={descriptionId}>
            {description}
          </span>
        ) : null}
        <span className="marker-dropzone__action">
          Choose file or drop here
        </span>
      </label>
      {selectedFile ? (
        <div
          aria-live="polite"
          className="marker-dropzone__preview"
          id={previewId}
        >
          <span className="marker-dropzone__file-name">
            {selectedFile.name}
          </span>
          <span className="marker-dropzone__file-size">
            {formatFileSize(selectedFile.size)}
          </span>
        </div>
      ) : null}
      {error ? <InlineError id={errorId}>{error}</InlineError> : null}
    </div>
  );
}
